import { Input, Option, Select } from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { TFunction } from 'i18next';
import { Dispatch, SetStateAction, useRef } from 'react';
import { useTranslation } from 'react-i18next';
// TODO: Lodash should no longer be used here
import omit from 'lodash/omit';
import { EnrollmentFormDataType } from '../../../enrollments';
import { toDateObject } from '../../../helpers/dates';

export const unenrollmentInitiatedByOptions = [
  'students.enrollment.unenrollmentInitiatedBy.parent',
  'students.enrollment.unenrollmentInitiatedBy.provider',
];

export const unenrollmentReasonOptions = [
  'students.enrollment.unenrollmentReason.familyMoves',
  'students.enrollment.unenrollmentReason.childAgedOutOfProgram',
  'students.enrollment.unenrollmentReason.childTransferredToOtherProgram',
  'students.enrollment.unenrollmentReason.cost',
  'students.enrollment.unenrollmentReason.schedule',
  'students.enrollment.unenrollmentReason.unmetNeeds',
  'students.enrollment.unenrollmentReason.dissatisfaction',
  'students.enrollment.unenrollmentReason.ownProgramCloses',
  'students.enrollment.unenrollmentReason.shortTermEnrollment',
  'students.enrollment.unenrollmentReason.other',
];

interface UnenrollmentReasonsProps {
  setFormData: Dispatch<SetStateAction<EnrollmentFormDataType>>;
  setErrors: Dispatch<SetStateAction<Record<string, string>>>;
  unenrollmentDate?: string;
  unenrollmentInitiatedBy?: string;
  unenrollmentReason?: string;
  isEnrolled?: boolean;
  onChangeDate: (_: any, { name, value, valid }: any) => void;
  errors: {
    unenrollmentDate?: string;
    unenrollmentInitiatedBy?: string;
    unenrollmentReason?: string;
  };
  enrollmentDate?: Date;
}

dayjs.extend(customParseFormat);
function createComboboxOptionTranslator(t: TFunction): (key: string) => Option {
  return function translateToOption(key: string) {
    const translation = t(key);
    return { label: translation, value: translation };
  };
}

function formatToInputDate(value?: string): string {
  if (!value) return '';
  const m = dayjs(value, 'MM/DD/YYYY', true);
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
}

function formatFromInputDate(value?: string): { display: string; valid: boolean } {
  if (!value) return { display: '', valid: true };
  const m = dayjs(value, 'YYYY-MM-DD', true);
  return { display: m.isValid() ? m.format('MM/DD/YYYY') : value, valid: m.isValid() };
}

export function StudentFormUnenrollmentReasons({
  setFormData,
  setErrors,
  onChangeDate,
  errors,
  unenrollmentDate,
  unenrollmentInitiatedBy,
  unenrollmentReason,
  isEnrolled,
  enrollmentDate,
}: UnenrollmentReasonsProps) {
  const { t } = useTranslation();
  const toComboboxOption = createComboboxOptionTranslator(t);
  const translatedUnenrollmentReasonOptions = unenrollmentReasonOptions.map(toComboboxOption);
  const translatedUnenrollmentInitiatedByOptions = unenrollmentInitiatedByOptions.map(toComboboxOption);
  const initiatedByWrapperRef = useRef<HTMLDivElement | null>(null);
  const reasonWrapperRef = useRef<HTMLDivElement | null>(null);

  const minUnenrollmentInputDate = (() => {
    if (!enrollmentDate) return undefined;
    const asDate = toDateObject(enrollmentDate);
    if (!asDate) return undefined;
    // Use UTC components to avoid local timezone shifting the calendar day. A bit overcooked but it does work.
    const year = asDate.getUTCFullYear();
    const month = asDate.getUTCMonth();
    const day = asDate.getUTCDate();
    const nextUtc = new Date(Date.UTC(year, month, day + 1));
    const y = nextUtc.getUTCFullYear();
    const m = String(nextUtc.getUTCMonth() + 1).padStart(2, '0');
    const d = String(nextUtc.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  })();

  function onSelectChange(fieldName: 'unenrollmentInitiatedBy' | 'unenrollmentReason', option: Option) {
    setFormData((prev) => ({ ...prev, [fieldName]: String(option.value) }));
    setErrors((prev) => omit(prev, fieldName));
  }

  return (
    <div className="mb-4 flex w-full flex-col gap-4">
      <h3 data-testid="unenrollmentSectionLabel">{t('enrollments.unenrollmentSectionLabel')}</h3>
      <div className="w-full" data-testid="unenrollmentDate-field">
        <Input
          name="unenrollmentDate"
          label={t('enrollments.unenrollmentDateLabel')}
          inputType="date"
          value={formatToInputDate(unenrollmentDate)}
          required={!isEnrolled}
          onChange={(event) => {
            const { display, valid } = formatFromInputDate(event.target.value);
            onChangeDate(null, { name: 'unenrollmentDate', value: display, valid });
          }}
          data-testid="unenrollmentDate"
          error={errors.unenrollmentDate && t(errors.unenrollmentDate)}
          min={minUnenrollmentInputDate}
        />
      </div>
      <div ref={initiatedByWrapperRef} className="w-full" data-testid="unenrollmentInitatedBy-field-error">
        <Select
          label={t('enrollments.unenrollmentInitiatedByLabel')}
          name="unenrollmentInitiatedBy"
          options={translatedUnenrollmentInitiatedByOptions}
          placeholder={t('enrollments.unenrollmentInitiatedByPlaceholder')}
          value={
            (unenrollmentInitiatedBy && { value: unenrollmentInitiatedBy, label: unenrollmentInitiatedBy }) || undefined
          }
          onChange={(opt) => onSelectChange('unenrollmentInitiatedBy', opt)}
          data-testid="unenrollmentInitiatedBy"
          error={errors.unenrollmentInitiatedBy && t(errors.unenrollmentInitiatedBy)}
        />
      </div>
      <div ref={reasonWrapperRef} className="w-full" data-testid="unenrollmentReason-field-error">
        <Select
          label={t('enrollments.unenrollmentReasonLabel')}
          name="unenrollmentReason"
          options={translatedUnenrollmentReasonOptions}
          placeholder={t('enrollments.unenrollmentReasonPlaceholder')}
          value={(unenrollmentReason && { value: unenrollmentReason, label: unenrollmentReason }) || undefined}
          onChange={(opt) => onSelectChange('unenrollmentReason', opt)}
          data-testid="unenrollmentReason"
          error={errors.unenrollmentReason && t(errors.unenrollmentReason)}
        />
      </div>
    </div>
  );
}
